import { useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { toast } from "sonner";

import ACL_RELATIONSHIPS from "acl-relationships";
import { BaseForm, FormInput, FormToggle } from "components/BaseForm";
import { REQUIRED_MARK } from "components/BaseForm/fields/fieldClasses";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import RichTextEditor from "components/RichEditor/RichTextEditor";
import useAuthorized from "hooks/useAuthorized";
import { useNoteTemplateMutations } from "hooks/useNoteTemplateTable";
import usePageTitle from "hooks/usePageTitle";
import NoteTemplateService from "services/NoteTemplate";
import { NoteTemplateFormSkeleton } from "routes/note_template/NoteTemplateFormSkeleton";
import { NOTE_TEMPLATES_PATH } from "routes/note_template/NoteTemplateTableConstants";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  enabled: z.boolean(),
  content_html: z.string().min(1, "Content is required"),
  editor_state: z.any().nullable().optional(),
});

function NoteTemplateEditorField({ model }) {
  const editor = useRef();
  const {
    setValue,
    formState: { errors },
  } = useFormContext();
  const error = errors.content_html;

  // The template stores both shapes: content_html is what the note form
  // inserts, editor_state is what this page reopens. Both are read off the
  // editor on change rather than carried in the change payload, so an
  // unchanged document costs no serialisation.
  const handleStateChange = useCallback(
    ({ hasText }) => {
      // An emptied editor still serialises to markup — a paragraph holding a
      // line break — which would satisfy the required check on content_html.
      // The document's own text is what decides whether it is empty.
      setValue(
        "content_html",
        hasText ? editor.current.exportStateAsHtml() : "",
        { shouldValidate: true }
      );
      setValue("editor_state", editor.current.exportState());
    },
    [setValue]
  );

  return (
    <Field data-invalid={error ? true : undefined}>
      <FieldLabel className={REQUIRED_MARK}>Content</FieldLabel>
      <RichTextEditor
        ref={editor}
        content={model?.editor_state ? undefined : model?.content_html}
        editorState={model?.editor_state}
        onStateChange={handleStateChange}
        placeholder="Write your note template content..."
        ariaLabel="Content"
      />
      <FieldError>{error?.message}</FieldError>
    </Field>
  );
}

NoteTemplateEditorField.propTypes = {
  model: PropTypes.shape({
    content_html: PropTypes.string,
    editor_state: PropTypes.object,
  }),
};

function NoteTemplateForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit";
  const { create, update } = useNoteTemplateMutations();

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.noteTemplate.edit
      : ACL_RELATIONSHIPS.noteTemplate.create,
  ]);

  const {
    data: noteTemplate,
    isPending,
    isError: templateError,
  } = useQuery({
    queryKey: ["note_template", id],
    queryFn: () => NoteTemplateService.get(id),
    enabled: isEdit,
    retry: false,
    // The form and the editor read their initial values once, on mount. A
    // cached entry from a previous visit would mount them with pre-edit data,
    // so the entry is dropped as soon as this page unmounts.
    gcTime: 0,
  });

  const pageTitle = isEdit ? "Edit Note Template" : "New Note Template";
  usePageTitle(pageTitle);

  const initialValues = useMemo(() => {
    if (!isEdit) {
      return {
        name: "",
        enabled: true,
        content_html: "",
        editor_state: null,
      };
    }
    return (
      noteTemplate && {
        name: noteTemplate.name,
        enabled: noteTemplate.enabled,
        content_html: noteTemplate.content_html,
        editor_state: noteTemplate.editor_state,
      }
    );
  }, [isEdit, noteTemplate]);

  const handleSubmit = async values => {
    const data = {
      name: values.name,
      enabled: values.enabled,
      content_html: values.content_html,
      editor_state: values.editor_state,
    };
    if (isEdit) {
      await update.mutateAsync({ id, data });
    } else {
      await create.mutateAsync(data);
    }
    toast.success("Note template saved");
    navigate(NOTE_TEMPLATES_PATH);
  };

  const handleCancel = () => navigate(NOTE_TEMPLATES_PATH);

  if (!canWrite) {
    return <Navigate to={NOTE_TEMPLATES_PATH} replace />;
  }

  if (templateError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-[var(--color-text-muted)]">
        <p>{"Couldn't load this note template. Please try again."}</p>
        <Link to={NOTE_TEMPLATES_PATH}>Back to Note Templates</Link>
      </div>
    );
  }

  if (isEdit && isPending) {
    return <NoteTemplateFormSkeleton />;
  }

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center tw:font-sans tw:text-[var(--color-text-primary)]">
      <div className="tw:flex tw:w-full tw:max-w-6xl tw:flex-col tw:gap-4">
        <BaseForm
          enablePageTitle
          enablePageTitleActions
          pageTitle={pageTitle}
          pageSubtitle={isEdit ? noteTemplate.name : undefined}
          pageTitleActions={<FormToggle name="enabled" label="Enabled" />}
          backLink={{
            to: NOTE_TEMPLATES_PATH,
            label: "Back to Note Templates",
          }}
          schema={schema}
          defaultValues={initialValues}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitButtonText="Save"
          ignoreEnterKey
        >
          <Card>
            <CardHeader>
              <CardTitle>Template</CardTitle>
            </CardHeader>
            <CardContent className="tw:flex tw:flex-col tw:gap-5">
              <FormInput name="name" label="Name" required />
              <NoteTemplateEditorField model={initialValues} />
            </CardContent>
          </Card>
        </BaseForm>
      </div>
    </div>
  );
}

NoteTemplateForm.propTypes = {
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
};

export default NoteTemplateForm;
